// Range token → explicit date window for endpoints that take
// /stats/dates/{start}/{end} (the Market Intelligence report rails).
//
// Covers the FULL shared range vocabulary (RANGES in territory-common) plus
// `custom_YYYYMMDD_YYYYMMDD` tokens from the global period control. Kept
// dependency-free so it can be unit-tested outside the bundle.
//
// Returns null for windows that cannot be expressed as explicit dates
// (`all_time`) and for unknown tokens. Callers MUST handle null — the
// previous behavior of silently defaulting to "this month to date" is what
// made the MI page show Sep 1–11 under a "Jan 1 – Jun 30" header (2026-09-11).

const CUSTOM_RE = /^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/;
function fmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parse(s) {
  const [y, m, d] = s.split('-').map(n => parseInt(n, 10));
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Sunday-start weeks, matching the global control's label (moment startOf('week')).
function startOfWeek(d) {
  return addDays(d, -d.getDay());
}
export function isCustomRange(rangeKey) {
  return CUSTOM_RE.test(String(rangeKey || ''));
}

// Local-date pair [start, end] (inclusive) for a custom token, else null.
export function parseCustomRange(rangeKey) {
  const m = String(rangeKey || '').match(CUSTOM_RE);
  if (!m) return null;
  return [new Date(+m[1], +m[2] - 1, +m[3]), new Date(+m[4], +m[5] - 1, +m[6])];
}

// Inclusive local-date pair [start, end] for a range token, or null.
export function rangeToDates(rangeKey, now = new Date()) {
  const custom = parseCustomRange(rangeKey);
  if (custom) return custom;
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = new Date(y, m, now.getDate());
  switch (rangeKey) {
    case 'today':
      return [today, today];
    case 'yesterday':
      return [addDays(today, -1), addDays(today, -1)];
    // Rolling trailing week: the 7 complete days before today.
    case 'last_7_days':
      return [addDays(today, -7), addDays(today, -1)];
    case 'this_week':
      return [startOfWeek(today), today];
    case 'last_week':
      {
        const s = addDays(startOfWeek(today), -7);
        return [s, addDays(s, 6)];
      }
    case 'this_month':
      return [new Date(y, m, 1), today];
    case 'last_month':
      return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    case 'this_quarter':
      return [new Date(y, Math.floor(m / 3) * 3, 1), today];
    case 'last_quarter':
      {
        const q = Math.floor(m / 3) * 3 - 3;
        return [new Date(y, q, 1), new Date(y, q + 3, 0)];
      }
    case 'this_year':
      return [new Date(y, 0, 1), today];
    case 'last_year':
      return [new Date(y - 1, 0, 1), new Date(y - 1, 11, 31)];
    case 'all_time':
    default:
      return null;
  }
}

// { start, end } as yyyy-MM-dd strings, or null.
export function rangeToWindow(rangeKey, now = new Date()) {
  const pair = rangeToDates(rangeKey, now);
  return pair ? {
    start: fmt(pair[0]),
    end: fmt(pair[1])
  } : null;
}

// Comparison window for a range + comparison type. `previous_year` shifts
// both ends back one calendar year; anything else shifts back by the window
// length (inclusive). Returns null when the current window is null.
export function previousWindow(rangeKey, comparison, now = new Date()) {
  const current = rangeToWindow(rangeKey, now);
  if (!current) return null;
  const start = parse(current.start);
  const end = parse(current.end);
  if (comparison === 'previous_year' || comparison === 'prev_year') {
    const ps = new Date(start);
    ps.setFullYear(ps.getFullYear() - 1);
    const pe = new Date(end);
    pe.setFullYear(pe.getFullYear() - 1);
    return {
      start: fmt(ps),
      end: fmt(pe)
    };
  }
  const ms = end.getTime() - start.getTime();
  const pe = addDays(start, -1);
  const ps = new Date(pe.getTime() - ms);
  return {
    start: fmt(ps),
    end: fmt(pe)
  };
}

// Human label for a window: "Jan 1, 2026 – Jun 30, 2026".
export function windowLabel(win) {
  if (!win) return '';
  const f = s => parse(s).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  return `${f(win.start)} – ${f(win.end)}`;
}
export default {
  isCustomRange,
  parseCustomRange,
  rangeToDates,
  rangeToWindow,
  previousWindow,
  windowLabel
};